import { Link } from '@/shared/models';

export interface Message {
    id?: string;
    groupId?: string;
    subject: string;
    startPublish?: Date;
    endPublish?: Date;
    validFrom?: Date;
    validTo?: Date;
    body?: string;
    subjectEn?: string;
    bodyEn?: string;
    pushNotifications: boolean | null;
    notificationType?: MessageNotificationType;
    status: MessageStatus;
    createdAt?: string;
    channels: Array<string>;
    receivers: Receivers;
    structuredMessage?: {
        templateId: string;
        input: {
            [key: string]: string;
        };
        choice: {
            [key: string]: string;
        };
        comment?: string;
    };
    hasChangedSincePublish: boolean;
    author?: string;
    sourceSystem?: string;
    auditEntries: Array<MessageAudit>;
    shouldPublish: boolean;
}

export interface MessageAudit {
    id: string;
    messageId: string;
    companyNumber: string;
    messageAuthor: MessageAuthor;
    operation: AuditOperation;
    createdAt: string;
}

export interface MessageAuthor {
    username: string;
    firstName: string;
    lastName: string;
}

export enum AuditOperation {
    None = 'None',
    Create = 'Create',
    Edit = 'Edit',
    Delete = 'Delete',
    Publish = 'Publish',
    RePublish = 'RePublish',
    Unpublish = 'Unpublish'
}

export interface MessageListItem {
    id: string;
    groupId: string;
    subject: string;
    categoryNameNo: string;
    startPublish: Date;
    endPublish: Date;
    validFrom: Date;
    validTo: Date;
    publishedAt: Date;
    sentTo?: string;
    status: MessageStatus;
    channels?: Array<string>;
    departureTime?: string;
    destination?: string;
    departure?: string;
    lineName?: string;
    reasonNo: string;
    line?: string;
    driverId?: string;
    notificationType: MessageNotificationType;
    author?: string;
    createdAt: string;
    templateId?: string;
    structuredMessage: StructuredMessage;
    links: Link[];
}

export interface MessageListGroupItem {
    groupId: string;
    categoryName: string;
    categoryNameNo: string;
    reason: string;
    reasonNo: string;
    status: MessageStatus;
    hasMixedStatus: boolean;
    startPublish: Date;
    endPublish: Date;
    validFrom: Date;
    validTo: Date;
    createdAt: string;
    author: string;
    sentTo?: string;
    messages: MessageListItem[];
    links: Link[];
}

export interface MessageListFilters {
    status: string;
    lineName: string;
    companyNumber: string;
    categoryName: string;
}

export interface MessageSourceLink {
    href: string;
    source: MessageSource;
    translationKey: string;
}

export interface StructuredMessage {
    templateId: string;
    input: {
        [key: string]: string;
    };
    choice: {
        [key: string]: string;
    };
    comment?: string;
}

export interface Receiver {
    id: string;
    name?: string;
}

export interface StopReceiver extends Receiver {
    fullName: string;
    publicCode: string;
}

export interface DriverReceiver extends Receiver {
    companyNumber: string;
    companyName: string;
}

export interface DepartureReceiver extends Receiver {
    departureTime: Date;
    arrivalTime: Date;
    operatingDay: string;
    calendarDay: string;
    departure: string;
    destination: string;
    lineName: string;
    companyNumber: string;
}

export interface LineReceiver extends Receiver {
    transportType: string;
}

export interface Receivers {
    scopes: Array<string>;
    lines: Array<LineReceiver>;
    stops: Array<StopReceiver>;
    areas: Array<Receiver>;
    companies: Array<Receiver>;
    drivers: Array<DriverReceiver>;
    departures: Array<DepartureReceiver>;
}

export const Scopes = {
    Areas: 'Areas',
    Departures: 'Departures',
    Drivers: 'Drivers',
    Lines: 'Lines',
    Companies: 'Companies',
    Stops: 'Stops'
};

export enum MessageStatus {
    Published = 'Published',
    Unpublished = 'Unpublished'
}

export enum MessageNotificationType {
    Information = 'Information',
    Warning = 'Warning',
    Cancellation = 'Cancellation',
    EarlyTripEnd = 'EarlyTripEnd',
    EquipmentFailure = 'EquipmentFailure',
    Full = 'Full',
    OnboardTicketMachine = 'OnboardTicketMachine',
    AtStopOther = 'AtStopOther'
}

export interface MessageTemplate {
    id: string;
    name: string;
    label: string;
    categoryName: string;
    categoryLabel: string;
    messageScopes: MessageScope[];
    editable: boolean;
    pushAvailable: boolean;
    transportTypes: TransportType[];
    notificationType: MessageNotificationType;
    inputTemplates: MessageInputTemplate[];
    choiceTemplates: MessageChoiceTemplate[];
    isFirstLevel: boolean;
    pushNotifications?: boolean;
}

export interface MessageInputTemplate {
    name: string;
    label: string;
    inputType: MessageInputType;
}

export interface MessageChoiceTemplate {
    name: string;
    label: string;
    group: string;
    groupLabel: string;
}

export interface MessageCategoryTemplate {
    id: string;
    name: string;
    label: string;
}

export const CancellationCategoryName = 'Cancellation';

export interface ServiceJourney {
    tripId: string;
    lineNumber: string;
    lineName: string;
    departure: string;
    destination: string;
    departureAt: Date;
    arrivalAt: Date;
    operatingDay: string;
    calendarDay: string;
    companyNumber: string;
    status: TripStatus;
}

export enum MessageInputType {
    ShortText = 'ShortText',
    Number = 'Number'
}

export enum MessageScope {
    Global = 'Global',
    Lines = 'Lines',
    Departures = 'Departures',
    Stops = 'Stops',
    Areas = 'Areas',
    Companies = 'Companies',
    Drivers = 'Drivers'
}

export enum MessageSource {
    AvvikPortal = 'AvvikPortal',
    TicketingMachine = 'TicketingMachine',
    OperatorEditor = 'OperatorEditor'
}

export enum TransportType {
    None = 0,
    Bus = 1,
    Water = 2,
    Rail = 3,
    Bike = 4,
    Unknown = 5
}

export type DepartureGroup = {
    operatingDay: string;
    variants: DepartureVariant[];
};

export type DepartureVariant = {
    departure: string;
    destination: string;
    lineName: string;
    companyNumber: string;
    departures: DepartureReceiver[];
};

export enum TripStatus {
    Unknown = 'Unknown',
    Cancelled = 'Cancelled'
}
